import React, { useMemo, useState } from "react";
import { formatFileSize } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileText,
  Download,
  Eye,
  Edit,
  Trash2,
  MoreHorizontal,
  Calendar,
  User,
  Tag,
  Folder,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useRole } from "@/hooks/useRole";

interface Document {
  id: number;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  mimeType: string;
  category: string;
  description?: string;
  tags: string[];
  ownerId: string;
  ownerType: string;
  status: string;
  uploadedAt: string;
  updatedAt: string;
  // Employee visibility (client documents) — flag + optional date window.
  visibleToEmployees?: boolean;
  visibilityStartDate?: string | null;
  visibilityEndDate?: string | null;
  sharedWithEmployer: boolean;
  sharedWithBroker: boolean;
  visibleToEmployee: boolean;
}

interface DocumentListProps {
  documents: Document[];
  onDocumentUpdate?: () => void;
  showActions?: boolean;
  showStatus?: boolean;
  folders?: { id: number; name: string }[];
  onMoveToFolder?: (
    documentId: number,
    folderId: number | null
  ) => Promise<void> | void;
  categoryOptions?: { value: string; label: string }[];
  emptyStateAction?: React.ReactNode;
  // keeps View/Download but hides edit/delete/move (showActions=false hides all)
  readOnly?: boolean;
  // override when "no documents" is misleading (e.g. root shows only unfiled docs)
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  // Broker/employer views: badge each client document's employee-visibility
  // state (visible / hidden / scheduled / expired) and expose the controls in
  // Edit. Off for the employee portal, where out-of-window docs never appear.
  showEmployeeVisibility?: boolean;
}

export function DocumentList({
  documents,
  onDocumentUpdate,
  showActions = true,
  showStatus = true,
  showEmployeeVisibility = false,
  folders = [],
  onMoveToFolder,
  categoryOptions,
  emptyStateAction,
  readOnly = false,
  emptyStateTitle = "No documents",
  emptyStateDescription = "Upload your first document to get started.",
}: DocumentListProps) {
  const [downloading, setDownloading] = useState<number | null>(null);
  const [viewing, setViewing] = useState<{ doc: Document; url: string } | null>(
    null
  );
  const [editing, setEditing] = useState<{
    doc: Document;
    category: string;
    description: string;
    visibleToEmployees: boolean;
    visibilityStartDate: string;
    visibilityEndDate: string;
    sharedWithEmployer: boolean;
    sharedWithBroker: boolean;
    visibleToEmployee: boolean;
  } | null>(null);
  const { toast } = useToast();
  // Which of the three visibility checkboxes to hide — same rule as document-upload.tsx.
  const { currentRole } = useRole();

  console.log(
    "DocumentList received documents:",
    documents,
    "count:",
    documents?.length
  );


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      license: "bg-blue-100 text-blue-800",
      insurance: "bg-green-100 text-green-800",
      policy: "bg-purple-100 text-purple-800",
      compliance: "bg-orange-100 text-orange-800",
      form: "bg-yellow-100 text-yellow-800",
      other: "bg-gray-100 text-gray-800",
      sbc: "bg-indigo-100 text-indigo-800",
      eoc: "bg-pink-100 text-pink-800",
      application: "bg-teal-100 text-teal-800",
      waiver: "bg-rose-100 text-rose-800",
    };
    return colors[category] || colors.other;
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes("pdf")) return "📄";
    if (mimeType.includes("word")) return "📝";
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet"))
      return "📊";
    if (mimeType.includes("image")) return "🖼️";
    return "📁";
  };

  const handleDownload = async (document: Document) => {
    setDownloading(document.id);

    try {
      const response = await fetch(`/api/documents/${document.id}/download`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to get download link");
      }

      const { downloadUrl } = await response.json();

      // Open download URL in new tab
      window.open(downloadUrl, "_blank");

      toast({
        title: "Download started",
        description: `Downloading ${document.originalFileName}`,
      });
    } catch (error: any) {
      console.error("Download error:", error);
      toast({
        title: "Download failed",
        description: error.message || "Failed to download document",
        variant: "destructive",
      });
    } finally {
      setDownloading(null);
    }
  };

  const handleView = async (document: Document) => {
    try {
      // Reuse download URL but render inline in a viewer for supported types
      const response = await fetch(
        `/api/documents/${document.id}/download?disposition=inline`,
        {
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Failed to get document URL");
      const { downloadUrl } = await response.json();

      if (
        document.mimeType.includes("pdf") ||
        document.mimeType.includes("image")
      ) {
        setViewing({ doc: document, url: downloadUrl });
      } else {
        window.open(downloadUrl, "_blank");
      }
    } catch (error: any) {
      console.error("View error:", error);
      toast({
        title: "Open failed",
        description: error.message || "Failed to open document",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (document: Document) => {
    if (
      !confirm(
        `Are you sure you want to delete "${document.originalFileName}"?`
      )
    ) {
      return;
    }

    try {
      const response = await fetch(`/api/documents/${document.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to delete document");
      }

      toast({
        title: "Document deleted",
        description: `${document.originalFileName} has been deleted`,
      });

      if (onDocumentUpdate) {
        onDocumentUpdate();
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete document",
        variant: "destructive",
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    try {
      const response = await fetch(`/api/documents/${editing.doc.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          category: editing.category,
          description: editing.description,
          ...(showEmployeeVisibility && editing.doc.ownerType === "client"
            ? {
                visibleToEmployees: editing.visibleToEmployees,
                visibilityStartDate: editing.visibilityStartDate || null,
                visibilityEndDate: editing.visibilityEndDate || null,
              }
            : {}),
          sharedWithEmployer: editing.sharedWithEmployer,
          sharedWithBroker: editing.sharedWithBroker,
          visibleToEmployee: editing.visibleToEmployee,
        }),
      });
      if (!response.ok) throw new Error("Failed to update document");
      toast({ title: "Document updated" , variant: "success"});
      setEditing(null);
      onDocumentUpdate?.();
    } catch (error: any) {
      toast({
        title: "Update failed",
        description: error.message || "Failed to update",
        variant: "destructive",
      });
    }
  };

  const defaultCategoryOptions = useMemo(
    () => [
      { value: "sbc", label: "SBC" },
      { value: "eoc", label: "EOC" },
      { value: "application", label: "Application" },
      { value: "waiver", label: "Waiver" },
      { value: "form", label: "Form" },
      { value: "policy", label: "Policy" },
      { value: "compliance", label: "Compliance" },
      { value: "license", label: "License" },
      { value: "insurance", label: "Insurance" },
      { value: "other", label: "Other" },
    ],
    []
  );

  const availableCategoryOptions = categoryOptions || defaultCategoryOptions;

  if (documents.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {emptyStateTitle}
          </h3>
          <p className="text-gray-500 text-center mb-4">
            {emptyStateDescription}
          </p>
          {emptyStateAction}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4">
      {/* Inline viewer for PDFs/images */}
      {viewing && (
        <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
          <DialogContent className="max-w-4xl w-[90vw] h-[85vh] p-0">
            <DialogHeader className="px-6 py-4">
              <DialogTitle className="truncate">
                {viewing.doc.originalFileName}
              </DialogTitle>
            </DialogHeader>
            <div className="w-full h-full">
              <iframe
                src={viewing.url}
                className="w-full h-[70vh]"
                title="Document Viewer"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit modal */}
      {editing && (
        <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={editing.category}
                  onValueChange={(v) =>
                    setEditing((e) => (e ? { ...e, category: v } : e))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCategoryOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editing.description}
                  onChange={(e) =>
                    setEditing((ed) =>
                      ed ? { ...ed, description: e.target.value } : ed
                    )
                  }
                  rows={3}
                />
              </div>
              {showEmployeeVisibility && editing.doc.ownerType === "client" && (
                <div className="rounded-md border p-3 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label htmlFor="edit-visible-to-employees" className="cursor-pointer">
                        Visible to employees
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Requires the folder to be shared with employees too.
                      </p>
                    </div>
                    <input
                      id="edit-visible-to-employees"
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={editing.visibleToEmployees}
                      onChange={(e) =>
                        setEditing((ed) =>
                          ed ? { ...ed, visibleToEmployees: e.target.checked } : ed
                        )
                      }
                    />
                  </div>
                  {editing.visibleToEmployees && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Visible from (optional)</Label>
                        <Input
                          type="date"
                          value={editing.visibilityStartDate}
                          onChange={(e) =>
                            setEditing((ed) =>
                              ed ? { ...ed, visibilityStartDate: e.target.value } : ed
                            )
                          }
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Visible until (optional)</Label>
                        <Input
                          type="date"
                          value={editing.visibilityEndDate}
                          onChange={(e) =>
                            setEditing((ed) =>
                              ed ? { ...ed, visibilityEndDate: e.target.value } : ed
                            )
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              {editing.doc.ownerType === "employee" && (
                <div className="space-y-2">
                  <Label>Visible to</Label>
                  {currentRole !== "employer" && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="editShareWithEmployer"
                        checked={editing.sharedWithEmployer}
                        onCheckedChange={(checked) =>
                          setEditing((ed) =>
                            ed ? { ...ed, sharedWithEmployer: checked === true } : ed
                          )
                        }
                      />
                      <Label htmlFor="editShareWithEmployer" className="font-normal cursor-pointer">
                        Employer
                      </Label>
                    </div>
                  )}
                  {currentRole !== "broker" && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="editShareWithBroker"
                        checked={editing.sharedWithBroker}
                        onCheckedChange={(checked) =>
                          setEditing((ed) =>
                            ed ? { ...ed, sharedWithBroker: checked === true } : ed
                          )
                        }
                      />
                      <Label htmlFor="editShareWithBroker" className="font-normal cursor-pointer">
                        Broker
                      </Label>
                    </div>
                  )}
                  {currentRole !== "employee" && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="editVisibleToEmployee"
                        checked={editing.visibleToEmployee}
                        onCheckedChange={(checked) =>
                          setEditing((ed) =>
                            ed ? { ...ed, visibleToEmployee: checked === true } : ed
                          )
                        }
                      />
                      <Label htmlFor="editVisibleToEmployee" className="font-normal cursor-pointer">
                        Employee
                      </Label>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit}>Save</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {documents.map((document) => (
        <Card key={document.id}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <div className="text-2xl">{getFileIcon(document.mimeType)}</div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="font-medium text-sm truncate">
                      {document.originalFileName}
                    </h3>
                    <Badge
                      className={`text-xs ${getCategoryColor(
                        document.category
                      )}`}
                    >
                      {document.category}
                    </Badge>
                    {showEmployeeVisibility &&
                      document.ownerType === "client" &&
                      (() => {
                        const today = new Date().toISOString().slice(0, 10);
                        const start = (document.visibilityStartDate || "").slice(0, 10);
                        const end = (document.visibilityEndDate || "").slice(0, 10);
                        if (document.visibleToEmployees === false)
                          return (
                            <Badge variant="secondary" className="text-xs">
                              Hidden from employees
                            </Badge>
                          );
                        if (start && today < start)
                          return (
                            <Badge variant="outline" className="text-xs text-blue-700 border-blue-200 bg-blue-50">
                              Employees see from {start}
                            </Badge>
                          );
                        if (end && today > end)
                          return (
                            <Badge variant="outline" className="text-xs text-amber-700 border-amber-200 bg-amber-50">
                              Expired from employee view {end}
                            </Badge>
                          );
                        return (
                          <Badge variant="outline" className="text-xs text-green-700 border-green-200 bg-green-50">
                            Visible to employees
                          </Badge>
                        );
                      })()}
                  </div>

                  {document.description && (
                    <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                      {document.description}
                    </p>
                  )}

                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <User className="h-3 w-3" />
                      <span>{formatFileSize(document.fileSize)}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3" />
                      <span>Uploaded {formatDate(document.uploadedAt)}</span>
                    </span>
                    {document.tags.length > 0 && (
                      <span className="flex items-center space-x-1">
                        <Tag className="h-3 w-3" />
                        <span>{document.tags.join(", ")}</span>
                      </span>
                    )}
                  </div>

                  {showStatus && (
                    <div className="flex items-center space-x-2 mt-2">
                      <Badge
                        variant={
                          document.status === "active" ? "default" : "secondary"
                        }
                        className="text-xs"
                      >
                        {document.status}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>

              {showActions && (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleView(document)}
                    className="h-8 w-8 p-0"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(document)}
                    disabled={downloading === document.id}
                    className="h-8 w-8 p-0"
                  >
                    {downloading === document.id ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>

                  {!readOnly && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {folders.length > 0 && (
                        <>
                          <div className="px-2 py-1 text-xs text-muted-foreground">
                            Move to folder
                          </div>
                          {folders.map((f) => (
                            <DropdownMenuItem
                              key={f.id}
                              onClick={async () => {
                                try {
                                  await onMoveToFolder?.(document.id, f.id);
                                  onDocumentUpdate?.();
                                  toast({ title: `Moved to ${f.name}` });
                                } catch (e: any) {
                                  toast({
                                    title: "Move failed",
                                    description: e.message,
                                    variant: "destructive",
                                  });
                                }
                              }}
                            >
                              <span className="flex items-center gap-2">
                                <Folder className="h-4 w-4" />
                                <span>{f.name}</span>
                              </span>
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuItem
                            onClick={async () => {
                              try {
                                await onMoveToFolder?.(document.id, null);
                                onDocumentUpdate?.();
                                toast({ title: "Removed from folder" , variant: "success"});
                              } catch (e: any) {
                                toast({
                                  title: "Move failed",
                                  description: e.message,
                                  variant: "destructive",
                                });
                              }
                            }}
                          >
                            Unfile
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <DropdownMenuItem
                        onClick={() =>
                          setEditing({
                            doc: document,
                            category: document.category,
                            description: document.description || "",
                            visibleToEmployees: document.visibleToEmployees !== false,
                            visibilityStartDate: (document.visibilityStartDate || "").slice(0, 10),
                            visibilityEndDate: (document.visibilityEndDate || "").slice(0, 10),
                            sharedWithEmployer: document.sharedWithEmployer,
                            sharedWithBroker: document.sharedWithBroker,
                            visibleToEmployee: document.visibleToEmployee,
                          })
                        }
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(document)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
